import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'antd';
import {
  ThunderboltFilled,
  ArrowRightOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  LoadingOutlined,
  RollbackOutlined,
  FormOutlined,
  CheckCircleFilled,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import useSpeechInput from './useSpeechInput';
import './aiCrew.css';

// Group paired start and end questions into a natural single conversational query
function formatClarifications(clarifications) {
  if (!Array.isArray(clarifications)) return [];
  const hasStart = clarifications.some((c) => c.field === 'start');
  const hasEnd = clarifications.some((c) => c.field === 'end');

  if (hasStart && hasEnd) {
    const list = [];
    let timePaired = false;
    for (const c of clarifications) {
      if (c.field === 'start' || c.field === 'end') {
        if (!timePaired) {
          list.push({
            field: 'timeRange',
            question: 'What are the shift start and end times? (e.g. 8:00 AM to 4:00 PM)',
            options: [],
          });
          timePaired = true;
        }
      } else {
        list.push(c);
      }
    }
    return list;
  }
  return clarifications;
}

/**
 * ClarifyConversation - Natural conversational chat flow for clarifying ambiguous crew requests.
 * Replaces disconnected dropdowns and full-screen loaders with an interactive agent thread.
 */
export default function ClarifyConversation({
  conversation = [],
  onClarify,
  isClarifying = false,
  onFallbackToManual,
  onRestart,
  currentCrew,
}) {
  const [answerText, setAnswerText] = useState('');
  const [replyError, setReplyError] = useState(null);
  const sendingRef = useRef(false);
  const [showLongWaitNotice, setShowLongWaitNotice] = useState(false);
  const threadEndRef = useRef(null);
  const textareaRef = useRef(null);
  const latestAgentIndex = conversation.reduce((last, turn, index) => turn.role === 'agent' ? index : last, -1);
  const pending = formatClarifications(conversation[latestAgentIndex]?.clarifications || []);
  const savedDetails = [currentCrew?.event?.label, currentCrew?.task?.label,
    currentCrew?.date, currentCrew?.start && `Starts ${currentCrew.start}`, currentCrew?.end && `Ends ${currentCrew.end}`,
    currentCrew?.headcount != null && `${currentCrew.headcount} people`, currentCrew?.venue, currentCrew?.crewLeadName].filter(Boolean);

  const speech = useSpeechInput({
    enabled: !isClarifying,
    sessionKey: conversation.length,
    onResult: (transcript) => {
      setAnswerText((prev) => (prev ? `${prev} ${transcript}` : transcript));
    },
  });

  // Auto-scroll to bottom whenever a new turn arrives or while agent is thinking
  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation, isClarifying]);

  // Notice after 5 seconds of clarifying
  useEffect(() => {
    if (!isClarifying) {
      setShowLongWaitNotice(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowLongWaitNotice(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isClarifying]);

  // Focus textarea when component mounts or agent finishes turn
  useEffect(() => {
    if (!isClarifying && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isClarifying]);

  const isValid = answerText.trim().length > 0;

  const handleSubmit = async () => {
    if (onClarify && isValid && !isClarifying && !sendingRef.current) {
      sendingRef.current = true; setReplyError(null);
      speech.stop();
      const text = answerText.trim();
      try { await onClarify(text); setAnswerText(''); }
      catch { setReplyError('Your reply could not be processed. Your text is still here; try again or restart this request.'); }
      finally { sendingRef.current = false; }
    }
  };

  const handleKeyDown = (e) => {
    if (e.isComposing || e.nativeEvent?.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      if (isValid && !isClarifying) {
        e.preventDefault();
        handleSubmit();
      }
    }
  };

  const handleSelectOption = async (field, option) => {
    if (isClarifying || !onClarify || sendingRef.current) return;
    sendingRef.current = true; setReplyError(null);
    speech.stop();
    try { await onClarify({ answers: { [field]: option.id }, label: option.label }); }
    catch { setReplyError('That choice could not be saved. Please try again.'); }
    finally { sendingRef.current = false; }
  };

  return (
    <div className="ai-crew-chat-wrapper" data-testid="ai-crew-clarify-panel">
      {savedDetails.length > 0 && <section className="ai-crew-known-details" aria-label="Saved request details">
        <strong>Details saved</strong>
        <div>{savedDetails.map((detail, i) => <span key={`${detail}-${i}`}>{detail}</span>)}</div>
        <small>You can correct any of these in your next reply.</small>
      </section>}
      {/* Scrollable Conversation Stream */}
      <div className="ai-crew-chat-thread" data-testid="ai-crew-chat-thread" role="log" aria-label="Crew Call conversation" aria-live="polite" aria-relevant="additions text">
        {conversation.map((turn, index) => {
          const isAgent = turn.role === 'agent';

          return (
            <div
              key={turn.id || index}
              className={`ai-crew-chat-turn ${isAgent ? 'ai-crew-chat-turn--agent' : 'ai-crew-chat-turn--user'}`}
            >
              {isAgent && (
                <div className="ai-crew-chat-turn-avatar">
                  <ThunderboltFilled />
                </div>
              )}

              <div className="ai-crew-chat-bubble-card">
                <div className="ai-crew-chat-sender-header">
                  <span className="ai-crew-chat-sender-name">
                    {isAgent ? 'CrewCall Agent' : 'You'}
                  </span>
                  {turn.timestamp && (
                    <span className="ai-crew-chat-timestamp">{turn.timestamp}</span>
                  )}
                </div>

                {turn.content && (
                  <div className="ai-crew-chat-turn-text">{turn.content}</div>
                )}

                {/* Clarification Questions & Suggestion Options */}
                {isAgent && Array.isArray(turn.clarifications) && turn.clarifications.length > 0 && (
                  <div className="ai-crew-chat-questions-list">
                    {formatClarifications(turn.clarifications).map((cl, qIdx) => (
                      <div key={cl.field || qIdx} className="ai-crew-chat-question-item">
                        <div className="ai-crew-chat-question-label">
                          <span className="ai-crew-chat-bullet">•</span>
                          <span>{cl.question}</span>
                        </div>

                        {/* Quick-select chips if options provided */}
                        {Array.isArray(cl.options) && cl.options.length > 0 && (
                          <div className="ai-crew-chat-chips-wrap">
                            {cl.options.map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                className="ai-crew-chat-chip"
                                onClick={() => handleSelectOption(cl.field, opt)}
                                disabled={isClarifying || index !== latestAgentIndex}
                                title={`Choose ${opt.label}`}
                              >
                                <span className="ai-crew-chat-chip-plus">+</span>
                                <span>{opt.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* In-Place Agent Thinking Indicator (NO full screen reload!) */}
        {isClarifying && (
          <div className="ai-crew-chat-turn ai-crew-chat-turn--agent">
            <div className="ai-crew-chat-turn-avatar ai-crew-chat-turn-avatar--pulse">
              <ThunderboltFilled />
            </div>
            <div className="ai-crew-chat-bubble-card ai-crew-chat-thinking-card">
              <div className="ai-crew-chat-thinking-content">
                <LoadingOutlined spin style={{ fontSize: 16, color: '#1677ff', marginRight: 8 }} />
                <span className="ai-crew-chat-thinking-text">
                  Saving your answers and checking what’s still needed…
                </span>
              </div>
              {showLongWaitNotice && (
                <div
                  className="ai-crew-chat-long-wait-notice"
                  style={{ marginTop: 8, color: '#64748b', fontSize: 12, fontStyle: 'italic' }}
                >
                  Your reply is still processing. Please wait before sending another reply.
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={threadEndRef} />
      </div>

      {/* Docked Reply Bar */}
      <div className="ai-crew-chat-reply-dock">
        {replyError && <p role="alert" className="ai-crew-reply-error">{replyError}</p>}
        <p className="ai-crew-reply-guidance">Pick a current option to save it, or type any missing details. Nothing is scheduled until you review and confirm.</p>
        <div className="ai-crew-chat-input-container">
          <textarea
            aria-label="Reply to Crew Call"
            maxLength={500}
            ref={textareaRef}
            rows={1}
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isClarifying}
            placeholder={
              speech.listening
                ? 'Listening to speech...'
                : pending.length === 1 ? pending[0].question : 'Answer any question below, or correct a saved detail…'
            }
            className="ai-crew-chat-textarea"
            data-testid="ai-crew-clarify-reply"
          />

          <div className="ai-crew-chat-input-actions">
            {speech.supported && (
              <button
                type="button"
                className={`ai-crew-mic-btn ${speech.listening ? 'ai-crew-mic-btn--active' : ''}`}
                onClick={() => (speech.listening ? speech.stop() : speech.start())}
                title={speech.listening ? 'Stop listening' : 'Speak your response'}
                aria-label={speech.listening ? 'Stop listening' : 'Speak your response'}
                disabled={isClarifying}
              >
                {speech.listening ? <AudioMutedOutlined /> : <AudioOutlined />}
              </button>
            )}

            <Button
              type="primary"
              size="middle"
              onClick={handleSubmit}
              disabled={!isValid || isClarifying}
              loading={isClarifying}
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              className="ai-crew-chat-send-btn"
            >
              Send
            </Button>
          </div>
        </div>

        {/* Auxiliary Keyboard & Manual Escape Hatch */}
        <div className="ai-crew-chat-footer-hints">
          <span className="ai-crew-chat-hint-kbd">
            Press <kbd>Enter</kbd> to send
          </span>
          {onRestart && <button type="button" className="ai-crew-chat-inline-link" disabled={isClarifying} onClick={onRestart}>Start over</button>}

          {onFallbackToManual && (
            <span className="ai-crew-chat-hint-manual">
              Prefer manual forms?{' '}
              <button
                type="button"
                className="ai-crew-chat-inline-link"
                onClick={onFallbackToManual}
              >
                Switch to standard crew wizard
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
